import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Chart } from 'chart.js';
import { ChartRendererSupabase } from '../charts/ChartRendererSupabase';
import { PolicyImpactResults, PolicyScenario } from '../../services/policyImpactService';
import { generatePolicyTimeSeriesData, TimeSeriesEmissionData } from '../../services/policyTimeSeriesService';
import './PolicyImpactChart.css';
import { supabaseClient } from '../../services/supabase';

interface PolicyImpactChartProps {
  results: PolicyImpactResults | null;
  loading: boolean;
  scenario: PolicyScenario;
  location: string;
  chartType?: 'line' | 'contributions' | 'forecast' | 'stacked';
  targetReductionPercent?: number;
  targetBaselineYear?: number;
}

// Cache for time series data to avoid redundant calculations
const timeSeriesCache = new Map<string, {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
}>();

// Create a stable key from scenario and location
function createScenarioKey(location: string, scenario: PolicyScenario): string {
  return `${location}:${scenario.evAdoptionPercent}:${scenario.vmtReductionPercent}:${scenario.heatPumpAdoptionPercent}:${scenario.solarCapacityMultiplier}:${scenario.hdvElectrificationPercent}:${scenario.vmtReductionComponents?.transitOrientedDevelopment || 0}:${scenario.vmtReductionComponents?.activeTransportation || 0}:${scenario.vmtReductionComponents?.teleworkPrograms || 0}:${scenario.vmtReductionComponents?.transitImprovements || 0}:${scenario.parkingPricing?.priceIncreasePercent || 0}:${scenario.micromobility?.scalePercent || 0}`;
}

const PolicyImpactChart: React.FC<PolicyImpactChartProps> = ({ results, loading, scenario, location, chartType = 'line', targetReductionPercent = 80, targetBaselineYear = 2005 }) => {
  const [timeSeriesData, setTimeSeriesData] = useState<{
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  } | null>(null);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);

  // Forecast datasets (BAU vs With Policies) with wedge areas (VMT, EV)
  const [forecastData, setForecastData] = useState<null | {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>(null);

  // Create stable scenario key for memoization
  const scenarioKey = useMemo(() => createScenarioKey(location, scenario), [location, scenario]);

  // Generate time series data when scenario or location changes
  useEffect(() => {
    const generateTimeSeries = async () => {
      if (!location) return;
      
      // Check cache first
      const cacheKey = scenarioKey;
      if (timeSeriesCache.has(cacheKey)) {
        const cached = timeSeriesCache.get(cacheKey);
        if (cached) {
          setTimeSeriesData(cached);
          return;
        }
      }
      
      setTimeSeriesLoading(true);
      try {
        const data = await generatePolicyTimeSeriesData(location, scenario);
        // Cache the result
        timeSeriesCache.set(cacheKey, data);
        // Limit cache size to prevent memory issues
        if (timeSeriesCache.size > 10) {
          const firstKey = timeSeriesCache.keys().next().value;
          timeSeriesCache.delete(firstKey);
        }
        setTimeSeriesData(data);
      } catch (error) {
        console.error('Error generating time series data:', error);
      } finally {
        setTimeSeriesLoading(false);
      }
    };

    generateTimeSeries();
  }, [location, scenarioKey]);

  // Cache for forecast data
  const forecastCache = useRef(new Map<string, {
    years: number[];
    bauTotals: number[];
    withPoliciesTotals: number[];
    vmtWedge: number[];
    evWedge: number[];
  }>());

  // Build forecast data whenever chart type or scenario changes
  useEffect(() => {
    if (chartType !== 'forecast' || !location || !results) {
      setForecastData(null);
      return;
    }

    const buildForecast = async () => {
      try {
        // Create cache key for forecast
        const forecastCacheKey = `${scenarioKey}:forecast:${results.baseline.currentEVPercent}`;
        
        // Check cache first
        if (forecastCache.current.has(forecastCacheKey)) {
          const cached = forecastCache.current.get(forecastCacheKey);
          if (cached) {
            setForecastData(cached);
            return;
          }
        }

        // Helper: zero-out all non-transport levers so BAU and isolated cases are unaffected by unrelated policies
        const zeroedPolicies = {
          heatPumpAdoptionPercent: 0,
          solarCapacityMultiplier: 1,
          hdvElectrificationPercent: 0,
          vmtReductionComponents: (scenario as any).vmtReductionComponents || {
            transitOrientedDevelopment: 0,
            activeTransportation: 0,
            transitImprovements: 0,
          },
          parkingPricing: (scenario as any).parkingPricing || { priceIncreasePercent: 0, vmtAffectedPercent: 0, commuteByCarPercent: 0, basePricePerHour: 0 },
          micromobility: (scenario as any).micromobility || { scalePercent: 0, tripsPerResident: 0, percentBike: 0, percentScooter: 0, milesPerBikeTrip: 0, milesPerScooterTrip: 0, percentBikeTripsDisplaceCars: 0, percentScooterTripsDisplaceCars: 0, kwhPerMileBike: 0, kwhPerMileScooter: 0, yearImplemented: 2025 },
        } as Partial<PolicyScenario>;

        // BAU (no VMT reduction, no added EV beyond current, all other policy levers neutral)
        const bauScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const bauKey = createScenarioKey(location, bauScenario);
        let bau = timeSeriesCache.get(bauKey);
        if (!bau) {
          bau = await generatePolicyTimeSeriesData(location, bauScenario);
          timeSeriesCache.set(bauKey, bau);
        }

        // VMT only
        const vmtOnlyScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: scenario.vmtReductionPercent || 0,
          evAdoptionPercent: results?.baseline.currentEVPercent || 0,
        } as PolicyScenario;
        const vmtOnlyKey = createScenarioKey(location, vmtOnlyScenario);
        let vmtOnly = timeSeriesCache.get(vmtOnlyKey);
        if (!vmtOnly) {
          vmtOnly = await generatePolicyTimeSeriesData(location, vmtOnlyScenario);
          timeSeriesCache.set(vmtOnlyKey, vmtOnly);
        }

        // EV only
        const evOnlyScenario: PolicyScenario = {
          ...scenario,
          ...zeroedPolicies,
          vmtReductionPercent: 0,
          evAdoptionPercent: scenario.evAdoptionPercent || (results?.baseline.currentEVPercent || 0),
        } as PolicyScenario;
        const evOnlyKey = createScenarioKey(location, evOnlyScenario);
        let evOnly = timeSeriesCache.get(evOnlyKey);
        if (!evOnly) {
          evOnly = await generatePolicyTimeSeriesData(location, evOnlyScenario);
          timeSeriesCache.set(evOnlyKey, evOnly);
        }

        // With both (current scenario)
        const bothScenario: PolicyScenario = {
          ...scenario,
          heatPumpAdoptionPercent: scenario.heatPumpAdoptionPercent || 0,
          solarCapacityMultiplier: scenario.solarCapacityMultiplier || 1,
          hdvElectrificationPercent: scenario.hdvElectrificationPercent || 0,
        } as PolicyScenario;
        const bothKey = createScenarioKey(location, bothScenario);
        let both = timeSeriesCache.get(bothKey);
        if (!both) {
          both = await generatePolicyTimeSeriesData(location, bothScenario);
          timeSeriesCache.set(bothKey, both);
        }

        const yrs = bau.projectedData.map(r => r.year);
        const sumCore = (row: TimeSeriesEmissionData) => (row.lightDutyEmissions + row.heavyDutyEmissions + row.naturalGasEmissions + row.electricityEmissions);

        // Compute "Other" baseline (everything not LDV/HDV/NG/Electricity) using current results baseline
        const coreBaseline =
          (results?.baseline.lightDutyGHG || 0) +
          (results?.baseline.heavyDutyGHG || 0) +
          (results?.baseline.naturalGasGHG || 0) +
          (timeSeriesData?.baselineData?.[timeSeriesData.baselineData.length - 1]?.electricityEmissions || 0);
        const otherBaseline = Math.max(0, (results?.baseline.totalGHG || 0) - coreBaseline);

        // Fetch population series and project to requested years (fallback: flat)
        let popByYear: Record<number, number> = {};
        try {
          const { data: popRows } = await supabaseClient
            .from('population')
            .select('year, population')
            .eq('location_name', location)
            .order('year', { ascending: true });
          if (popRows && popRows.length > 0) {
            popRows.forEach((r: any) => { popByYear[r.year] = r.population; });
            // Simple linear extrapolation to 2045 if needed
            const yearsAvail = Object.keys(popByYear).map(y => parseInt(y)).sort((a,b)=>a-b);
            const lastYear = yearsAvail[yearsAvail.length - 1];
            const prevYear = yearsAvail[yearsAvail.length - 2] || lastYear - 1;
            const growth = lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;
            const neededYears = yrs.filter(y => !(y in popByYear));
            neededYears.forEach(y => { popByYear[y] = popByYear[lastYear] + growth * (y - lastYear); });
          }
        } catch (e) {
          console.warn('Population lookup failed; using flat other emissions', e);
        }
        const basePop = popByYear[2023] || popByYear[Object.keys(popByYear).map(n=>parseInt(n)).sort((a,b)=>b-a)[0]] || 1;
        const otherByYear = yrs.map(y => Math.round(otherBaseline * (popByYear[y] ? popByYear[y] / basePop : 1)));

        const sumTotals = (rows: TimeSeriesEmissionData[]) => rows.map((r, i) => Math.round(sumCore(r) + (otherByYear[i] || 0)));

        const bauTotals = sumTotals([...bau.baselineData, ...bau.projectedData].slice(bau.baselineData.length));
        const vmtOnlyTotals = sumTotals([...vmtOnly.baselineData, ...vmtOnly.projectedData].slice(vmtOnly.baselineData.length));
        const evOnlyTotals = sumTotals([...evOnly.baselineData, ...evOnly.projectedData].slice(evOnly.baselineData.length));
        const bothTotals = sumTotals([...both.baselineData, ...both.projectedData].slice(both.baselineData.length));

        // Construct wedges for forecast horizon
        const vmtWedge = bauTotals.map((v, i) => Math.max(0, v - vmtOnlyTotals[i]));
        const evWedge = vmtOnlyTotals.map((v, i) => Math.max(0, v - bothTotals[i]));

        const forecastResult = { years: yrs, bauTotals, withPoliciesTotals: bothTotals, vmtWedge, evWedge };
        
        // Cache the result
        forecastCache.current.set(forecastCacheKey, forecastResult);
        // Limit cache size
        if (forecastCache.current.size > 10) {
          const firstKey = forecastCache.current.keys().next().value;
          forecastCache.current.delete(firstKey);
        }
        
        setForecastData(forecastResult);
      } catch (e) {
        console.warn('Failed to build forecast data', e);
        setForecastData(null);
      }
    };
    buildForecast();
  }, [chartType, location, scenarioKey, results?.baseline.currentEVPercent]);

  // Memoize chart data preparation to avoid recalculation on every render
  // Must be called before any early returns (React Hooks rule)
  const chartData = useMemo(() => {
    if (!timeSeriesData) return null;

    // Combine baseline and projected data to get all years
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    
    // Create maps for easy lookup
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    
    // Find max value for vertical line height
    const allValues = [
      ...timeSeriesData.baselineData.map(d => d.lightDutyEmissions + d.heavyDutyEmissions + d.naturalGasEmissions + d.electricityEmissions),
      ...timeSeriesData.projectedData.map(d => d.lightDutyEmissions + d.heavyDutyEmissions + d.naturalGasEmissions + d.electricityEmissions)
    ];
    const maxValue = Math.max(...allValues, 0);
    
    // Create data arrays aligned with all years
    const baselineData = {
      lightDuty: uniqueYears.map(year => baselineMap.get(year)?.lightDutyEmissions || null),
      heavyDuty: uniqueYears.map(year => baselineMap.get(year)?.heavyDutyEmissions || null),
      naturalGas: uniqueYears.map(year => baselineMap.get(year)?.naturalGasEmissions || null),
      electricity: uniqueYears.map(year => baselineMap.get(year)?.electricityEmissions || null)
    };
    const projectedData = {
      lightDuty: uniqueYears.map(year => projectedMap.get(year)?.lightDutyEmissions || null),
      heavyDuty: uniqueYears.map(year => projectedMap.get(year)?.heavyDutyEmissions || null),
      naturalGas: uniqueYears.map(year => projectedMap.get(year)?.naturalGasEmissions || null),
      electricity: uniqueYears.map(year => projectedMap.get(year)?.electricityEmissions || null)
    };

    return {
    labels: uniqueYears.map((y) => y.toString()),
    datasets: [
      {
        label: 'LDV (Baseline)',
        data: baselineData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 4,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'LDV (With Policies)',
        data: projectedData.lightDuty,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas (Baseline)',
        data: baselineData.naturalGas,
        backgroundColor: 'rgba(16, 185, 129, 0.3)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 4,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Natural Gas (With Policies)',
        data: projectedData.naturalGas,
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity (Baseline)',
        data: baselineData.electricity,
        backgroundColor: 'rgba(245, 158, 11, 0.3)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 4,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
      {
        label: 'Electricity (With Policies)',
        data: projectedData.electricity,
        backgroundColor: 'rgba(245, 158, 11, 0.8)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 5,
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 5,
      },
    ],
  };
  }, [timeSeriesData]);

  // Stacked area chart data - only "With Policies" for LDV, Natural Gas, and Electricity
  const stackedChartData = useMemo(() => {
    if (!timeSeriesData) return null;

    // Combine baseline and projected data to get all years
    const allYears = [...timeSeriesData.baselineData.map(d => d.year), ...timeSeriesData.projectedData.map(d => d.year)];
    const uniqueYears = Array.from(new Set(allYears)).sort((a, b) => a - b);
    
    // Create maps for easy lookup
    const baselineMap = new Map(timeSeriesData.baselineData.map(d => [d.year, d]));
    const projectedMap = new Map(timeSeriesData.projectedData.map(d => [d.year, d]));
    
    // Get "With Policies" data for the three categories
    const projectedData = {
      lightDuty: uniqueYears.map(year => projectedMap.get(year)?.lightDutyEmissions || null),
      naturalGas: uniqueYears.map(year => projectedMap.get(year)?.naturalGasEmissions || null),
      electricity: uniqueYears.map(year => projectedMap.get(year)?.electricityEmissions || null)
    };

    // Calculate target line value based on baseline year
    const baselineYearData = baselineMap.get(targetBaselineYear);
    const baselineYearTotal = baselineYearData 
      ? baselineYearData.lightDutyEmissions + baselineYearData.heavyDutyEmissions + 
        baselineYearData.naturalGasEmissions + baselineYearData.electricityEmissions
      : 0;
    const targetValue = baselineYearTotal * (1 - targetReductionPercent / 100);
    
    // Create target line data (same value for all years)
    const targetLineData = uniqueYears.map(() => targetValue);

    return {
      labels: uniqueYears.map((y) => y.toString()),
      datasets: [
        {
          label: 'LDV (With Policies)',
          data: projectedData.lightDuty,
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 0, // Lower order - drawn first (behind)
        },
        {
          label: 'Natural Gas (With Policies)',
          data: projectedData.naturalGas,
          backgroundColor: 'rgba(16, 185, 129, 0.7)',
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 0, // Lower order - drawn first (behind)
        },
        {
          label: 'Electricity (With Policies)',
          data: projectedData.electricity,
          backgroundColor: 'rgba(245, 158, 11, 0.7)',
          borderColor: 'rgba(245, 158, 11, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.1,
          pointRadius: 0,
          pointHoverRadius: 5,
          stack: 'main',
          order: 0, // Lower order - drawn first (behind)
        },
        {
          label: `Target: ${targetReductionPercent}% below ${targetBaselineYear} levels`,
          data: targetLineData,
          backgroundColor: 'transparent',
          borderColor: 'rgba(0, 0, 0, 1)',
          borderWidth: 6, // Very thick line for maximum visibility
          fill: false,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 5,
          borderDash: [12, 6], // Long, prominent dashes
          order: 1, // Higher order - drawn last (on top)
          // No stack property - this line won't be stacked
          spanGaps: false,
        },
      ],
    };
  }, [timeSeriesData, targetReductionPercent, targetBaselineYear]);

  // Stacked chart options
  const stackedChartOptions = useMemo(() => ({
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 16,
        bottom: 32,
        left: 90,
        right: 20,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: false,
          padding: 15,
          boxWidth: 12,
          boxHeight: 12,
          font: {
            size: 11,
          },
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()} MT CO₂e`;
          },
        },
      },
      // Custom plugin to ensure target line is drawn on top after all datasets
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        const targetDatasetIndex = chart.data.datasets.findIndex((d: any) => d.label && d.label.includes('Target:'));
        if (targetDatasetIndex === -1) return;
        
        const meta = chart.getDatasetMeta(targetDatasetIndex);
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
        ctx.lineWidth = 6;
        ctx.setLineDash([12, 6]);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        
        // Draw the line through all points
        let firstPoint = true;
        for (let i = 0; i < meta.data.length; i++) {
          const point = meta.data[i];
          if (point && !isNaN(point.x) && !isNaN(point.y)) {
            if (firstPoint) {
              ctx.moveTo(point.x, point.y);
              firstPoint = false;
            } else {
              ctx.lineTo(point.x, point.y);
            }
          }
        }
        
        ctx.stroke();
        ctx.restore();
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Year',
        },
        offset: true,
        grid: {
          display: true,
          color: (context: any) => {
            const label = context.tick?.label;
            if (label === '2023') {
              return 'rgba(107, 114, 128, 0.6)';
            }
            return 'rgba(0, 0, 0, 0.06)';
          },
          lineWidth: (context: any) => {
            const label = context.tick?.label;
            return label === '2023' ? 2 : 1;
          },
        },
        ticks: {
          display: true,
          autoSkip: false,
          maxTicksLimit: 50,
          callback: function(value: any, index: number, ticks: any[]) {
            const labels = (stackedChartData as any)?.labels || [];
            if (labels.length === 0) return '';
            const label = labels[index];
            const yearNum = parseInt(String(label), 10);
            if (Number.isNaN(yearNum)) return '';
            
            // Exclude 2023 and 2024
            if (yearNum === 2023 || yearNum === 2024) {
              return '';
            }
            
            if (index === 0 || index === labels.length - 1) {
              return String(yearNum);
            }
            
            // Show every 5 years
            if (yearNum % 5 === 0) {
              return String(yearNum);
            }
            return '';
          },
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'metric tons CO₂e',
          font: {
            size: 12,
            weight: 'normal' as const,
            color: '#374151',
          },
          padding: {
            top: 0,
            bottom: 10,
            left: 0,
            right: 0,
          },
          color: '#374151',
        },
        ticks: {
          display: true,
          callback: function(value: any) {
            const numValue = typeof value === 'number' ? value : Number(value);
            if (Number.isNaN(numValue)) return '';
            return numValue.toLocaleString();
          },
          font: {
            size: 11,
          },
          padding: 8,
          autoSkip: false,
          maxTicksLimit: 10,
          color: '#374151',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: true,
          drawOnChartArea: true,
        },
      },
    },
  }), [stackedChartData]);

  // Build forecast chart data/options when available
  // Must be called before any early returns (React Hooks rule)
  const forecastChartData = useMemo(() => {
    if (!forecastData) return null;
    
    return (() => {
    const bau = forecastData.bauTotals;
    const afterVmt = forecastData.bauTotals.map((v, i) => v - forecastData.vmtWedge[i]);
    const afterEv = afterVmt.map((v, i) => v - forecastData.evWedge[i]);

    return {
      labels: forecastData.years,
      datasets: [
        // BAU line (top reference)
        {
          label: 'Total GHG (BAU)',
          data: bau,
          borderColor: 'rgba(31, 41, 55, 1)',
          backgroundColor: 'rgba(31, 41, 55, 0.1)',
          borderWidth: 4,
          pointRadius: 0,
          fill: false,
          tension: 0.2,
          order: 3,
        },
        // VMT wedge: area between BAU and After-VMT
        {
          label: 'VMT Reduction',
          data: afterVmt,
          borderColor: 'rgba(59, 130, 246, 0)',
          backgroundColor: 'rgba(59, 130, 246, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (BAU)
          },
          tension: 0.2,
          order: 2,
        },
        // EV wedge: area between After-VMT and After-EV (With Policies)
        {
          label: 'Vehicle Electrification',
          data: afterEv,
          borderColor: 'rgba(96, 165, 250, 0)',
          backgroundColor: 'rgba(96, 165, 250, 0.35)',
          pointRadius: 0,
          fill: {
            target: '-1', // fill to previous dataset (After-VMT)
          },
          tension: 0.2,
          order: 1,
        },
        // With policies line (remaining emissions)
        {
          label: 'Total GHG (With Policies)',
          data: afterEv,
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 5,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
          tension: 0.2,
          order: 4,
        },
      ]
    };
    })();
  }, [forecastData]);

  // Early returns must come after all hooks
  if (loading || timeSeriesLoading) {
    return (
      <div className="policy-impact-loading">
        <div className="loading-spinner"></div>
        <p>Calculating policy impacts...</p>
      </div>
    );
  }

  if (!results || !timeSeriesData) {
    return (
      <div className="policy-impact-placeholder">
        <p>Adjust policy levers to see impact on GHG emissions</p>
      </div>
    );
  }

  const forecastChartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 16,
        bottom: 32,
        left: 80,
        right: 40,
      },
    },
    plugins: {
      legend: { position: 'top' as const },
      tooltip: { intersect: false, mode: 'index' as const },
    },
    scales: {
      y: { title: { display: true, text: 'Emissions (Metric Tons CO₂e)' } },
      x: {
        title: { display: true, text: 'Year' },
        // Keep endpoints (e.g., 2005, 2045) fully visible
        offset: false,
      },
    },
  };

  const chartOptions = {
    type: 'line' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 16,
        bottom: 32,
        left: 90,   // room for Y-axis labels and title - increased to ensure visibility
        right: 20,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: false,
          padding: 15,
          boxWidth: 12,
          boxHeight: 12,
          font: {
            size: 11,
          },
          generateLabels: function(chart: any) {
            const original = Chart.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            
            labels.forEach((label: any, index: number) => {
              const dataset = chart.data.datasets[index];
              if (dataset.label && dataset.label.includes('With Policies')) {
                // Add custom rendering for dotted lines in legend
                label.lineDash = dataset.borderDash || [0, 0];
              }
              // Truncate long labels to prevent overflow
              if (label.text && label.text.length > 30) {
                label.text = label.text.substring(0, 27) + '...';
              }
            });
            
            return labels;
          }
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()} MT CO₂e`;
          },
        },
      },
      // Draw vertical line between 2023 and 2024 to indicate break between historical and forecast
      afterDatasetsDraw: (chart: any) => {
        const ctx = chart.ctx;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        
        if (!xScale || !yScale) return;
        
        // Find the position between 2023 and 2024
        const labels = chart.data.labels || [];
        const year2023Index = labels.findIndex((l: string) => l === '2023');
        const year2024Index = labels.findIndex((l: string) => l === '2024');
        
        if (year2023Index === -1 || year2024Index === -1) return;
        
        // Calculate x position as the midpoint between 2023 and 2024
        const tick2023 = xScale.getPixelForValue('2023');
        const tick2024 = xScale.getPixelForValue('2024');
        const xPosition = tick2023 !== undefined && tick2024 !== undefined 
          ? (tick2023 + tick2024) / 2 
          : xScale.getPixelForValue(2023.5);
        
        if (xPosition === undefined || isNaN(xPosition)) return;
        
        // Draw vertical line from top to bottom of chart area
        const topY = yScale.top;
        const bottomY = yScale.bottom;
        
        ctx.save();
        ctx.strokeStyle = 'rgba(107, 114, 128, 0.8)'; // Gray color matching the grid line
        ctx.lineWidth = 2;
        ctx.setLineDash([]); // Solid line
        ctx.beginPath();
        ctx.moveTo(xPosition, topY);
        ctx.lineTo(xPosition, bottomY);
        ctx.stroke();
        ctx.restore();
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
        },
        // Use offset to ensure first and last data points are fully visible
        offset: true,
        min: undefined, // Let Chart.js determine from data
        max: undefined, // Let Chart.js determine from data
        grid: {
          display: true,
          color: (context: any) => {
            // Highlight 2023 with a darker grid line
            const label = context.tick?.label;
            if (label === '2023') {
              return 'rgba(107, 114, 128, 0.6)';
            }
            return 'rgba(0, 0, 0, 0.06)';
          },
          lineWidth: (context: any) => {
            const label = context.tick?.label;
            return label === '2023' ? 2 : 1;
          },
        },
        ticks: {
          display: true,
          autoSkip: false,
          maxTicksLimit: 50, // Increased to show more years
          // Show labels every 5 years (2005, 2010, ...), and always show first/last
          // Exclude 2023 and 2024 from display
          callback: function(value: any, index: number, ticks: any[]) {
            const labels = (chartData as any)?.labels || [];
            if (labels.length === 0) return '';
            const label = labels[index];
            const yearNum = parseInt(String(label), 10);
            if (Number.isNaN(yearNum)) return '';
            
            // Exclude 2023 and 2024
            if (yearNum === 2023 || yearNum === 2024) {
              return '';
            }
            
            // Always show first and last year
            if (index === 0 || index === labels.length - 1) {
              return String(yearNum);
            }
            
            // Show every 5 years
            if (yearNum % 5 === 0) {
              return String(yearNum);
            }
            return '';
          },
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'metric tons CO₂e',
          font: {
            size: 12,
            weight: 'normal' as const,
            color: '#374151',
          },
          padding: {
            top: 0,
            bottom: 10,
            left: 0,
            right: 0,
          },
          color: '#374151',
        },
        ticks: {
          display: true,
          showLabelBackdrop: false,
          callback: function(value: any) {
            const numValue = typeof value === 'number' ? value : Number(value);
            if (Number.isNaN(numValue)) return '';
            return numValue.toLocaleString();
          },
          font: {
            size: 11,
          },
          padding: 8,
          autoSkip: false,
          maxTicksLimit: 10,
          color: '#374151',
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: true,
          drawOnChartArea: true,
        },
      },
    },
  };

  // Calculate total reduction for percentages
  const totalReduction = results.reductions.totalReduction;

  // Check if we have VMT component breakdown
  const hasVmtBreakdown = results.contributionsByPolicy.vmtReductionByComponent;
  const vmtComponents = results.contributionsByPolicy.vmtReductionByComponent;

  // Create labels with policy values and percentages
  const contributions = hasVmtBreakdown && vmtComponents
    ? [
        {
          name: 'Vehicle Electrification',
          value: Math.round(results.contributionsByPolicy.evAdoption),
          setting: `${scenario.evAdoptionPercent}%`,
        },
        {
          name: 'VMT: Transit-Oriented Dev',
          value: Math.round(vmtComponents.transitOrientedDevelopment),
          setting: `${scenario.vmtReductionComponents.transitOrientedDevelopment.toFixed(1)}%`,
        },
        {
          name: 'VMT: Active Transportation',
          value: Math.round(vmtComponents.activeTransportation),
          setting: `${scenario.vmtReductionComponents.activeTransportation.toFixed(1)}%`,
        },
        {
          name: 'VMT: Telework Programs',
          value: Math.round(vmtComponents.teleworkPrograms),
          setting: `${scenario.vmtReductionComponents.teleworkPrograms.toFixed(1)}%`,
        },
        {
          name: 'VMT: Transit Improvements',
          value: Math.round(vmtComponents.transitImprovements),
          setting: `${scenario.vmtReductionComponents.transitImprovements.toFixed(1)}%`,
        },
        {
          name: 'Parking Pricing',
          value: Math.round(results.contributionsByPolicy.parkingPricing),
          setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase`,
        },
        {
          name: 'Expand Micromobility',
          value: Math.round(results.contributionsByPolicy.micromobility || 0),
          setting: `${scenario.micromobility?.scalePercent || 0}% scale`,
        },
        {
          name: 'Heat Pumps',
          value: Math.round(results.contributionsByPolicy.heatPumpAdoption),
          setting: `${scenario.heatPumpAdoptionPercent}%`,
        },
        {
          name: 'Solar Capacity',
          value: Math.round(results.contributionsByPolicy.solarCapacity),
          setting: `${scenario.solarCapacityMultiplier}x`,
        },
        {
          name: 'HDV Electrification',
          value: Math.round(results.contributionsByPolicy.hdvElectrification),
          setting: `${scenario.hdvElectrificationPercent}%`,
        },
      ]
    : [
        {
          name: 'Vehicle Electrification',
          value: Math.round(results.contributionsByPolicy.evAdoption),
          setting: `${scenario.evAdoptionPercent}%`,
        },
        {
          name: 'VMT Reduction',
          value: Math.round(results.contributionsByPolicy.vmtReduction),
          setting: `${scenario.vmtReductionPercent}%`,
        },
        {
          name: 'Parking Pricing',
          value: Math.round(results.contributionsByPolicy.parkingPricing),
          setting: `${scenario.parkingPricing.priceIncreasePercent}% price increase`,
        },
        {
          name: 'Expand Micromobility',
          value: Math.round(results.contributionsByPolicy.micromobility || 0),
          setting: `${scenario.micromobility?.scalePercent || 0}% scale`,
        },
        {
          name: 'Heat Pumps',
          value: Math.round(results.contributionsByPolicy.heatPumpAdoption),
          setting: `${scenario.heatPumpAdoptionPercent}%`,
        },
        {
          name: 'Solar Capacity',
          value: Math.round(results.contributionsByPolicy.solarCapacity),
          setting: `${scenario.solarCapacityMultiplier}x`,
        },
        {
          name: 'HDV Electrification',
          value: Math.round(results.contributionsByPolicy.hdvElectrification),
          setting: `${scenario.hdvElectrificationPercent}%`,
        },
      ];

  // Prepare data for waterfall chart showing contributions
  const truncate = (text: string, max: number = 28) =>
    text.length > max ? `${text.slice(0, max - 1)}…` : text;
  const fmtTons = (v: number) => (v >= 1000 ? `${Math.round(v/100)/10}k` : `${Math.round(v)}`);

  const contributionsData = {
    labels: contributions.map((c) => truncate(`${c.name} (${c.setting}, ${fmtTons(c.value)} t)`)),
    datasets: [
      {
        label: 'GHG Reduction (MT CO₂e)',
        data: contributions.map((c) => c.value),
        backgroundColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 0.8)', // EV Adoption - Blue
              'rgba(5, 150, 105, 0.8)', // VMT: TOD - Green-600
              'rgba(16, 185, 129, 0.8)', // VMT: Active Transport - Green-500
              'rgba(52, 211, 153, 0.8)', // VMT: Telework - Green-400
              'rgba(110, 231, 183, 0.8)', // VMT: Transit - Green-300
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)', // Heat Pumps - Orange
              'rgba(139, 92, 246, 0.8)', // Solar - Purple
              'rgba(236, 72, 153, 0.8)', // HDV - Pink
            ]
          : [
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(34, 197, 94, 0.8)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 0.8)', // Micromobility - Green-700
              'rgba(245, 158, 11, 0.8)',
              'rgba(139, 92, 246, 0.8)',
              'rgba(236, 72, 153, 0.8)',
            ],
        borderColor: hasVmtBreakdown
          ? [
              'rgba(59, 130, 246, 1)',
              'rgba(5, 150, 105, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(52, 211, 153, 1)',
              'rgba(110, 231, 183, 1)',
              'rgba(34, 197, 94, 1)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 1)', // Micromobility - Green-700
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ]
          : [
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(34, 197, 94, 1)', // Parking Pricing - Green-600
              'rgba(22, 163, 74, 1)', // Micromobility - Green-700
              'rgba(245, 158, 11, 1)',
              'rgba(139, 92, 246, 1)',
              'rgba(236, 72, 153, 1)',
            ],
        borderWidth: 2,
      },
    ],
  };

  const contributionsOptions = {
    type: 'bar' as const,
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    layout: {
      padding: {
        top: 12,
        bottom: 16,
        left: 24,
        right: 24,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.x;
            const percentage = totalReduction > 0 ? ((value / totalReduction) * 100).toFixed(1) : '0';
            return [
              `Reduction: ${value.toLocaleString()} MT CO₂e`,
              `Contribution: ${percentage}% of total`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'GHG Reduction (Metric Tons CO₂e)',
        },
        ticks: {
          callback: (value: any) => Number(value).toLocaleString(),
        },
      },
      y: {
        ticks: {
          autoSkip: false,
          padding: 6,
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return chartType === 'line' ? (
    /* Line Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={chartData}
        chartOptions={chartOptions}
        height="400px"
        width="100%"
      />
    </div>
  ) : chartType === 'contributions' ? (
    /* Contributions Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={contributionsData}
        chartOptions={contributionsOptions}
        height="450px"
      />
    </div>
  ) : chartType === 'stacked' ? (
    /* Stacked Area Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={stackedChartData || { labels: [], datasets: [] }}
        chartOptions={stackedChartOptions}
        height="400px"
        width="100%"
      />
    </div>
  ) : (
    /* Forecast Chart */
    <div className="chart-section">
      <ChartRendererSupabase
        chartData={forecastChartData || { labels: [], datasets: [] }}
        chartOptions={forecastChartOptions}
        height="450px"
      />
    </div>
  );
};

export default PolicyImpactChart;
